import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
  PushPin as PushPinIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  Announcement,
  AnnouncementStatus,
  AnnouncementPriority,
  announcementService,
} from '../../services/announcementService'

interface AnnouncementTableProps {
  announcements: Announcement[]
  onEdit: (announcement: Announcement) => void
  onRefresh: () => void
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  onPageChange: (page: number) => void
}

export const AnnouncementTable: React.FC<AnnouncementTableProps> = ({
  announcements,
  onEdit,
  onRefresh,
  pagination,
  onPageChange,
}) => {
  const { t } = useTranslation('announcement')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, announcement: Announcement) => {
    setAnchorEl(event.currentTarget)
    setSelectedAnnouncement(announcement)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    if (selectedAnnouncement) {
      onEdit(selectedAnnouncement)
    }
    handleMenuClose()
  }

  const handleDelete = async () => {
    if (selectedAnnouncement) {
      try {
        await announcementService.deleteAnnouncement(selectedAnnouncement.id)
        onRefresh()
        setSnackbar({
          open: true,
          message: t('messages.deleteSuccess'),
          severity: 'success',
        })
      } catch (error) {
        console.error('Delete failed:', error)
        setSnackbar({
          open: true,
          message: t('messages.deleteError'),
          severity: 'error',
        })
      }
    }
    setDeleteDialogOpen(false)
    handleMenuClose()
  }

  const handlePublish = async () => {
    if (selectedAnnouncement) {
      try {
        await announcementService.publishAnnouncement(selectedAnnouncement.id)
        onRefresh()
      } catch (error) {
        console.error('Publish failed:', error)
      }
    }
    setPublishDialogOpen(false)
    handleMenuClose()
  }

  const handleArchive = async () => {
    if (selectedAnnouncement) {
      try {
        await announcementService.archiveAnnouncement(selectedAnnouncement.id)
        onRefresh()
      } catch (error) {
        console.error('Archive failed:', error)
      }
    }
    setArchiveDialogOpen(false)
    handleMenuClose()
  }

  const getStatusColor = (
    status: AnnouncementStatus
  ): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
      case 'DRAFT':
        return 'default'
      case 'SCHEDULED':
        return 'warning'
      case 'PUBLISHED':
        return 'success'
      case 'ARCHIVED':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityIcon = (priority: AnnouncementPriority) => {
    switch (priority) {
      case 'HIGH':
        return <WarningIcon color="error" fontSize="small" />
      default:
        return null
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('ja-JP')
  }

  if (announcements.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('table.noData')}</Typography>
      </Paper>
    )
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('table.title')}</TableCell>
              <TableCell>{t('table.targetRole')}</TableCell>
              <TableCell>{t('table.status')}</TableCell>
              <TableCell>{t('table.priority')}</TableCell>
              <TableCell>{t('table.readCount')}</TableCell>
              <TableCell>{t('table.createdAt')}</TableCell>
              <TableCell align="right">{t('table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {announcements.map((announcement) => (
              <TableRow key={announcement.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {announcement.isPinned && <PushPinIcon fontSize="small" color="primary" />}
                    {getPriorityIcon(announcement.priority)}
                    <Typography variant="body2">{announcement.title}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`targetRole.${announcement.targetRole}`)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`status.${announcement.status.toLowerCase()}`)}
                    size="small"
                    color={getStatusColor(announcement.status)}
                  />
                </TableCell>
                <TableCell>{t(`priority.${announcement.priority}`)}</TableCell>
                <TableCell>{announcement._count?.readLogs || 0}</TableCell>
                <TableCell>{formatDate(announcement.createdAt)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, announcement)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={pagination.limit}
        rowsPerPageOptions={[pagination.limit]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('manage.edit')}</ListItemText>
        </MenuItem>
        {selectedAnnouncement?.status !== 'PUBLISHED' && (
          <MenuItem onClick={() => setPublishDialogOpen(true)}>
            <ListItemIcon>
              <PublishIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('manage.publish')}</ListItemText>
          </MenuItem>
        )}
        {selectedAnnouncement?.status !== 'ARCHIVED' && (
          <MenuItem onClick={() => setArchiveDialogOpen(true)}>
            <ListItemIcon>
              <ArchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('manage.archive')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => setDeleteDialogOpen(true)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{t('manage.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('messages.confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{selectedAnnouncement?.title}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button onClick={handleDelete} color="error">
            {t('manage.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>{t('messages.confirmPublish')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{selectedAnnouncement?.title}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button onClick={handlePublish} color="primary">
            {t('manage.publish')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogTitle>{t('messages.confirmArchive')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{selectedAnnouncement?.title}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button onClick={handleArchive} color="primary">
            {t('manage.archive')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
